import { Box, Button } from '@mui/material'
import { SearchInput, SelectInput, useListContext } from 'react-admin'

import { formatMonths, formatSemiMonths } from '@/utils/dateTools'
import { useEffect, useRef, useState } from 'react'
import Select from '@/components/Select'

type Props = {
  setStartDate: (date: Date | null) => void
  setEndDate: (date: Date | null) => void
  minDate?: string
  label?: string
  source?: string
  alwaysOn?: boolean
}

type FilterType = 'Monthly' | 'Semi-Monthly' | 'All Time'

const EarningsFilter = ({
  setStartDate,
  setEndDate,
  source,
  minDate = '2011-01-01'
}: Props) => {
  const { setFilters, filterValues } = useListContext()
  const [localStartDate, setLocalStartDate] = useState('')
  const [localEndDate, setLocalEndDate] = useState('')
  const [localFilter, setLocalFilter] = useState<FilterType>('Monthly')
  const isInitialized = useRef(false)

  useEffect(() => {
    if (isInitialized.current || !filterValues) return

    const [start, end] = filterValues.earningsFilter?.split(',') || []

    if (start && end) {
      setStartDate(new Date(start))
      setEndDate(new Date(end))
    }

    isInitialized.current = true
  }, [filterValues, setStartDate, setEndDate])

  return (
    <Box sx={{ mb: 2 }}>
      <SearchInput source="saleName" sx={{ mb: 2 }} />

      <Box sx={{ display: 'flex', gap: 4 }}>
        <Select
          label="Filters"
          options={['Monthly', 'Semi-Monthly', 'All Time']}
          value={localFilter}
          onChange={(value) => {
            setLocalFilter(value as FilterType)
            setFilters({}, [])
          }}
          testId="earnings-filter-select"
        />

        {localFilter === 'Monthly' && (
          <SelectInput
            key="months"
            source={source}
            label="Months"
            choices={formatMonths({ startDate: minDate })}
            onChange={(e) => {
              const [start, end] = e.target.value.split(',')
              setStartDate(new Date(start))
              setEndDate(new Date(end))
            }}
          />
        )}
        {localFilter === 'Semi-Monthly' && (
          <SelectInput
            key="semiMonths"
            source={source}
            label="Semi Months"
            choices={formatSemiMonths({ startDate: minDate })}
            onChange={(e) => {
              const [start, end] = e.target.value.split(',')
              setStartDate(new Date(start))
              setEndDate(new Date(end))
            }}
          />
        )}

        {localFilter === 'All Time' && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            <label>Start Date</label>
            <input
              type="date"
              value={localStartDate}
              min={minDate}
              onChange={(e) => {
                setLocalStartDate(e.target.value)
              }}
              data-testid="start-date-input"
            />
            <label>End Date</label>
            <input
              type="date"
              value={localEndDate}
              min={minDate}
              onChange={(e) => {
                setLocalEndDate(e.target.value)
              }}
              data-testid="end-date-input"
            />
            <Button
              variant="contained"
              onClick={() => {
                setStartDate(localStartDate ? new Date(localStartDate) : null)
                setEndDate(localEndDate ? new Date(localEndDate) : null)
                setFilters({
                  sendEmpty: !(
                    Boolean(localStartDate) && Boolean(localEndDate)
                  ),
                  start_date_gte: localStartDate,
                  start_date_lte: localEndDate
                })
              }}
              data-testid="apply-dates"
            >
              Apply
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  )
}
export { EarningsFilter }
